import { apiRequest } from '../../../services/api/client';
import { ClassroomRecord } from '../types/classroom.types';

type ClassSegmentResource = {
  id: number;
  name: string;
  minimum_age: number | null;
  maximum_age: number | null;
};

type ClassResource = {
  id: number;
  segment_id: number | null;
  name: string;
  description: string | null;
  gender: string;
  teacher_member_ids?: number[];
  teacher_names: string[];
  active: boolean;
  segment?: ClassSegmentResource | null;
  students_count?: number;
  class_quarters_count?: number;
};

type ClassListResponse = {
  data: ClassResource[];
  meta: {
    current_page: number;
    last_page: number;
    total: number;
    has_more_pages: boolean;
  };
};

type ClassResponse = {
  data: ClassResource;
};

export interface SaveClassroomInput {
  title: string;
  segmentId: string | null;
  trimesterId?: string | null;
  gender: string;
  teacherNames: string[];
  teacherMemberIds: string[];
  active?: boolean;
}

const normalizeAgeRange = (segment?: ClassSegmentResource | null) => {
  if (!segment) return '';
  if (segment.minimum_age === null && segment.maximum_age === null) return '';
  if (segment.minimum_age !== null && segment.maximum_age !== null) {
    return `${segment.minimum_age} a ${segment.maximum_age} anos`;
  }
  if (segment.minimum_age !== null) return `${segment.minimum_age}+`;
  return `Ate ${segment.maximum_age} anos`;
};

const toClassroom = (classroom: ClassResource): ClassroomRecord => ({
  id: String(classroom.id),
  segmentId: classroom.segment_id ? String(classroom.segment_id) : null,
  title: classroom.name,
  category: classroom.segment?.name ?? 'Sem segmento',
  teachers: classroom.teacher_names ?? [],
  teacherMemberIds: (classroom.teacher_member_ids ?? []).map(String),
  students: classroom.students_count ?? 0,
  avg: '0%',
  ageRange: normalizeAgeRange(classroom.segment),
  gender: classroom.gender,
  active: classroom.active,
  type: 'Normal',
  room: '-',
  schedule: '-',
});

const toPayload = (input: SaveClassroomInput) => ({
  name: input.title,
  segment_id: input.segmentId ? Number(input.segmentId) : null,
  quarter_id: input.trimesterId ? Number(input.trimesterId) : undefined,
  gender: input.gender,
  teacher_member_ids: input.teacherMemberIds.map(Number),
  teacher_names: input.teacherNames,
  active: input.active ?? true,
});

export const classroomService = {
  async list(trimesterId?: string): Promise<ClassroomRecord[]> {
    const params = new URLSearchParams({ per_page: '100' });

    if (trimesterId) {
      params.set('quarter_id', trimesterId);
    }

    const firstPage = await apiRequest<ClassListResponse>(`/ebd/classes?${params.toString()}`);
    return firstPage.data.map(toClassroom);
  },

  async getById(classroomId: string, trimesterId?: string): Promise<ClassroomRecord | null> {
    const params = new URLSearchParams();

    if (trimesterId) {
      params.set('quarter_id', trimesterId);
    }

    const payload = await apiRequest<ClassResponse>(
      `/ebd/classes/${classroomId}${params.toString() ? `?${params.toString()}` : ''}`
    );
    return toClassroom(payload.data);
  },

  async create(input: SaveClassroomInput): Promise<ClassroomRecord> {
    const payload = await apiRequest<ClassResponse>('/ebd/classes', {
      method: 'POST',
      body: JSON.stringify(toPayload(input)),
    });

    return toClassroom(payload.data);
  },

  async update(classroomId: string, input: SaveClassroomInput): Promise<ClassroomRecord> {
    const payload = await apiRequest<ClassResponse>(`/ebd/classes/${classroomId}`, {
      method: 'PUT',
      body: JSON.stringify(toPayload(input)),
    });

    return toClassroom(payload.data);
  },

  async remove(classroomId: string): Promise<void> {
    await apiRequest<void>(`/ebd/classes/${classroomId}`, {
      method: 'DELETE',
    });
  },
};
